import React, { useState, useRef, useEffect } from 'react';
import { api } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import {
  Camera,
  Loader2,
  RefreshCw,
  Send,
  CheckCircle,
  AlertCircle,
  ScanLine,
  ShieldCheck,
  History,
} from 'lucide-react';
import { toast } from 'sonner';
import jsQR from 'jsqr';

const SLOTS = [
  { key: 'foto_depan', label: 'Depan' },
  { key: 'foto_belakang', label: 'Belakang' },
  { key: 'foto_kanan', label: 'Kanan' },
  { key: 'foto_kiri', label: 'Kiri' },
];

const PatroliPage = () => {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const checkingCodeRef = useRef(false);

  const [location, setLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);

  const [scanning, setScanning] = useState(false);
  const [checkingCode, setCheckingCode] = useState(false);
  const [activePost, setActivePost] = useState(null);

  const [cameraActive, setCameraActive] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [slotIndex, setSlotIndex] = useState(0);
  const [photos, setPhotos] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);

  const isSelfView = !['super_admin', 'hr', 'manager'].includes(user?.role);

  useEffect(() => {
    getLocation();
    fetchLogs();
    return () => stopCamera();
  }, []);

  const fetchLogs = async () => {
    try {
      const res = await api.get('/patrol/logs');
      setLogs(res.data);
    } catch (error) {
      console.error('Failed to fetch patrol logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const getLocation = () => {
    setGettingLocation(true);
    if (!navigator.geolocation) {
      setGettingLocation(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({ latitude: position.coords.latitude, longitude: position.coords.longitude });
        setGettingLocation(false);
      },
      () => setGettingLocation(false),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const openScanner = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Browser tidak mendukung akses kamera');
      return;
    }
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      streamRef.current = mediaStream;
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play();
      }
      setScanning(true);

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      scanIntervalRef.current = setInterval(() => {
        const video = videoRef.current;
        if (!video || video.readyState !== video.HAVE_ENOUGH_DATA || checkingCodeRef.current) return;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        if (code && code.data) {
          submitCode(code.data);
        }
      }, 350);
    } catch (error) {
      toast.error('Gagal akses kamera');
    }
  };

  const stopScanner = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setScanning(false);
  };

  const submitCode = async (code) => {
    if (checkingCodeRef.current) return;
    checkingCodeRef.current = true;
    setCheckingCode(true);
    try {
      const res = await api.get(`/security-posts/lookup/${code.trim().toUpperCase()}`);
      setActivePost(res.data);
      stopScanner();
      toast.success(`Pos ditemukan: ${res.data.nama}`);
    } catch (error) {
      toast.error('Kode pos tidak ditemukan');
    } finally {
      checkingCodeRef.current = false;
      setCheckingCode(false);
    }
  };

  const startCamera = async () => {
    setCameraLoading(true);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = mediaStream;
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = async () => {
          await videoRef.current.play();
          setCameraActive(true);
          setCameraLoading(false);
        };
      }
    } catch (error) {
      toast.error('Gagal akses kamera');
      setCameraLoading(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraActive(false);
  };

  const captureSlotPhoto = () => {
    if (!videoRef.current || !location) {
      toast.error('Lokasi belum siap, tunggu sebentar');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);

    const now = new Date();
    const dateStr = now.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' });
    const timeStr = now.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const coordStr = `${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}`;

    const barHeight = Math.max(50, canvas.height * 0.14);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(0, canvas.height - barHeight, canvas.width, barHeight);

    const fontSize = Math.max(12, Math.round(canvas.width * 0.032));
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${fontSize}px sans-serif`;
    ctx.textBaseline = 'middle';
    ctx.fillText(`${SLOTS[slotIndex].label} - ${dateStr} ${timeStr}`, 10, canvas.height - barHeight * 0.62);
    ctx.font = `${fontSize * 0.85}px sans-serif`;
    ctx.fillText(coordStr, 10, canvas.height - barHeight * 0.24);

    const photoData = canvas.toDataURL('image/jpeg', 0.75);
    const key = SLOTS[slotIndex].key;
    setPhotos((p) => ({ ...p, [key]: photoData }));
    stopCamera();

    if (slotIndex < SLOTS.length - 1) {
      setSlotIndex((i) => i + 1);
    }
  };

  const retakeSlot = (index) => {
    setSlotIndex(index);
    setPhotos((p) => {
      const copy = { ...p };
      delete copy[SLOTS[index].key];
      return copy;
    });
  };

  const allCaptured = SLOTS.every((s) => photos[s.key]);

  const handleSubmit = async () => {
    if (!allCaptured || !activePost || !location) return;
    setSubmitting(true);
    try {
      await api.post('/patrol/log', {
        kode_pos: activePost.kode_pos,
        latitude: location.latitude,
        longitude: location.longitude,
        ...photos,
      });
      toast.success('Laporan patroli berhasil dikirim');
      setActivePost(null);
      setPhotos({});
      setSlotIndex(0);
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengirim laporan patroli');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelPatrol = () => {
    stopCamera();
    stopScanner();
    setActivePost(null);
    setPhotos({});
    setSlotIndex(0);
  };

  return (
    <div className="space-y-6" data-testid="patrol-page">
      <div>
        <h1 className="text-2xl font-bold">Patroli Keamanan</h1>
        <p className="text-muted-foreground text-sm mt-1">Scan QR pos, lalu foto 4 arah (Depan/Belakang/Kanan/Kiri)</p>
      </div>

      {user?.employee_id ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><ScanLine className="w-5 h-5 text-primary" />Ronda Patroli</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!activePost ? (
              <div className="space-y-3">
                <div className="relative w-full max-w-[360px] mx-auto" style={{ aspectRatio: '4/3' }}>
                  <div className="absolute inset-0 bg-slate-900 rounded-lg overflow-hidden flex items-center justify-center">
                    <video ref={videoRef} autoPlay playsInline muted className={`w-full h-full object-cover ${scanning ? '' : 'hidden'}`} />
                    {!scanning && (
                      <Button size="sm" variant="secondary" onClick={openScanner} data-testid="open-scanner-btn">
                        <Camera className="w-4 h-4 mr-1" />Scan QR Pos
                      </Button>
                    )}
                  </div>
                </div>
                {checkingCode && (
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />Memvalidasi kode pos...
                  </div>
                )}
                {scanning && !checkingCode && (
                  <p className="text-center text-xs text-muted-foreground">Arahkan kamera ke QR pos keamanan</p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <Alert className="bg-green-50 border-green-500">
                  <ShieldCheck className="h-4 w-4 text-green-600" />
                  <AlertDescription className="flex items-center justify-between">
                    <span>Pos: <strong data-testid="active-post-name">{activePost.nama}</strong></span>
                    <Button size="sm" variant="ghost" onClick={cancelPatrol} data-testid="cancel-patrol-btn">Batal</Button>
                  </AlertDescription>
                </Alert>

                <div className="grid grid-cols-4 gap-2">
                  {SLOTS.map((s, idx) => (
                    <div key={s.key} className="text-center">
                      <div
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 ${idx === slotIndex && !photos[s.key] ? 'border-primary' : 'border-transparent'}`}
                        data-testid={`slot-${s.key}`}
                      >
                        {photos[s.key] ? (
                          <>
                            <img src={photos[s.key]} alt={s.label} className="w-full h-full object-cover" />
                            <button
                              className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5"
                              onClick={() => retakeSlot(idx)}
                              data-testid={`retake-${s.key}`}
                            >
                              <RefreshCw className="w-3 h-3 text-white" />
                            </button>
                          </>
                        ) : (
                          <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                            <Camera className="w-5 h-5 text-slate-400" />
                          </div>
                        )}
                      </div>
                      <p className="text-xs mt-1">{s.label}</p>
                    </div>
                  ))}
                </div>

                {!allCaptured && (
                  <div className="relative w-full max-w-[360px] mx-auto" style={{ aspectRatio: '4/3' }}>
                    <div className="absolute inset-0 bg-slate-900 rounded-lg overflow-hidden">
                      <video ref={videoRef} autoPlay playsInline muted className={`w-full h-full object-cover ${cameraActive ? '' : 'hidden'}`} />
                      {!cameraActive ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Button size="sm" variant="secondary" onClick={startCamera} disabled={cameraLoading} data-testid="open-slot-camera-btn">
                            {cameraLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Buka Kamera - Foto ${SLOTS[slotIndex].label}`}
                          </Button>
                        </div>
                      ) : (
                        <div className="absolute bottom-2 left-0 right-0 flex justify-center">
                          <Button size="sm" onClick={captureSlotPhoto} data-testid="capture-slot-btn">
                            <Camera className="w-4 h-4 mr-1" />Ambil Foto {SLOTS[slotIndex].label}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <Button className="w-full" onClick={handleSubmit} disabled={!allCaptured || submitting} data-testid="submit-patrol-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Kirim Laporan Patroli
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : !isSelfView ? null : (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Akun belum terhubung dengan data karyawan. Hubungi HR.</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="w-5 h-5 text-primary" />
            {isSelfView ? 'Riwayat Patroli Saya' : 'Semua Riwayat Patroli'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingLogs ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : logs.length === 0 ? (
            <p className="text-center text-muted-foreground py-6">Belum ada riwayat patroli</p>
          ) : (
            <div className="space-y-3" data-testid="patrol-log-list">
              {logs.map((log) => (
                <div key={log.id} className="border rounded-lg p-3" data-testid={`patrol-log-${log.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <Badge variant="secondary">{log.post_nama}</Badge>
                      {!isSelfView && <span className="text-xs text-muted-foreground ml-2">{log.employee_nama}</span>}
                    </div>
                    <span className="text-xs text-muted-foreground">{new Date(log.created_at).toLocaleString('id-ID')}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {SLOTS.map((s) => (
                      <img key={s.key} src={log[s.key]} alt={s.label} className="w-full aspect-square object-cover rounded" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PatroliPage;
